import { trim } from 'lodash';
import { type Permission } from '@common/api/permission';
import { fallbackLocale, type Locale } from '@common/i18n/supportedLocales';
import {
  type RetailerLevel,
  type UserAgreementsResponse,
  type UserData,
  UserLevel,
} from '@common/types/api';
import { type GUID, type IanaId } from '@common/types/brandedTypes';
import { IanaIdUtc } from '@common/utils/datetime/timezones';
import getUserFullName from '@common/utils/formatting/getUserFullName';
import getUserInitials from '@common/utils/formatting/getUserInitials';

const userLevelNames: { [key in UserLevel]: string; } = {
  [UserLevel.User]: 'user',
  [UserLevel.AdvancedUser]: 'advanced-user',
  [UserLevel.EnergyProvider]: 'energy-provider',
  [UserLevel.RetailerAdmin]: 'admin',
  [UserLevel.Technician]: 'technician',
  [UserLevel.Developer]: 'developer',
  [UserLevel.CountryManager]: 'country-manager',
  [UserLevel.SuperAdmin]: 'super-admin',
};

export const getUserLevelNameKey = (
  userLevel: UserLevel,
) => `common.user-role.${userLevelNames[userLevel]}`;

interface User {
  readonly id: GUID;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phoneNumber: string;
  readonly loginUsername: string;
  readonly timeZoneId: string;
  readonly ianaId: IanaId;
  readonly language: string;
  readonly locale: Locale;
  readonly countryCode: string;
  readonly fullName: (startFromLastName?: boolean) => string | undefined;
  readonly initials: (startFromLastName?: boolean) => string | undefined;
  readonly userLevel: UserLevel;
  readonly userLevelName: string;
  readonly countries?: Array<string>;
  readonly company?: string;
  readonly retailerId?: string;
  readonly retailerLevel?: RetailerLevel;
  readonly status: 'active' | 'deactivated';
  readonly lastLoginUtc?: string;
  readonly permissions?: Permission[];
  readonly hasAcceptedEula: boolean;
  readonly unverifiedEmail?: string;
  readonly agreements?: UserAgreementsResponse;
}

function getInitials(this: User, startFromLastName?: boolean): string | undefined {
  return getUserInitials(this, startFromLastName);
}

export function getFullName(this: User, startFromLastName?: boolean): string | undefined {
  return getUserFullName(this, startFromLastName);
}

export const UnknownUser: User = {
  id: '' as GUID,
  loginUsername: '',
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  timeZoneId: '',
  ianaId: IanaIdUtc,
  fullName: getFullName,
  initials: getInitials,
  language: '',
  locale: fallbackLocale,
  countryCode: 'DE',
  userLevel: UserLevel.User,
  userLevelName: userLevelNames[UserLevel.User],
  status: 'active',
  hasAcceptedEula: true,
};

export const TestUser: User = {
  id: 'aaa-bbb-ccc-ddd-eee' as GUID,
  loginUsername: 'testuser@email.com',
  email: 'testuser@email.com',
  firstName: 'Test',
  lastName: 'User',
  phoneNumber: '+1234567890',
  timeZoneId: 'FLE Standard Time',
  ianaId: 'Europe/Kiev' as IanaId,
  fullName: getFullName,
  initials: getInitials,
  language: 'de-DE',
  locale: fallbackLocale,
  countryCode: 'DE',
  userLevel: UserLevel.AdvancedUser,
  userLevelName: userLevelNames[UserLevel.AdvancedUser],
  status: 'active',
  hasAcceptedEula: true,
};

export const createUserFromResponse = (apiUser: UserData): User => {
  const { retailers: [retailer] = [] } = apiUser;
  let company;
  let userLevel = apiUser.levelId;
  let retailerId;
  let retailerLevel;
  if (userLevel >= UserLevel.Technician) {
    company = 'Stiebel Eltron';
  } else if (retailer) {
    company = retailer.name;
    userLevel = retailer.levelId;
    retailerId = retailer.id;
    retailerLevel = retailer.retailerLevel;
  }

  return {
    company,
    userLevel,
    retailerId,
    retailerLevel,
    id: apiUser.id,
    firstName: trim(apiUser.firstName),
    lastName: trim(apiUser.lastName),
    email: apiUser.email,
    phoneNumber: apiUser.phoneNumber,
    loginUsername: apiUser.userName,
    timeZoneId: apiUser.timeZone,
    ianaId: IanaIdUtc,
    fullName: getFullName,
    initials: getInitials,
    language: apiUser.culture,
    locale: fallbackLocale,
    countryCode: apiUser.countryCode,
    userLevelName: userLevelNames[userLevel],
    countries: apiUser.countryAccesses,
    status: apiUser.deactivatedUtc ? 'deactivated' : 'active',
    lastLoginUtc: apiUser.lastLoginUtc,
    permissions: apiUser.permissions,
    hasAcceptedEula: apiUser.hasAcceptedEula,
    unverifiedEmail: apiUser.lastEmailChangeVerification?.email,
    agreements: apiUser.agreements,
  };
};

export const isUnknownUser = (user: User | undefined): boolean => user === UnknownUser;

export const isUserLevelGreaterOrEqual = (
  user: User | undefined,
  level: UserLevel,
): boolean => !!user && user.userLevel >= level;

export default User;
